import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedCodeBackground from '../../components/base/AnimatedCodeBackground';

export default function Home() {
  const navigate = useNavigate();

  const [videos, setVideos] = useState({
    dna: null as string | null,
    blockchain: null as string | null,
    art: null as string | null
  });
  const [uploading, setUploading] = useState({
    dna: false,
    blockchain: false,
    art: false
  });

  const fileInputRefs = {
    dna: useRef<HTMLInputElement>(null),
    blockchain: useRef<HTMLInputElement>(null),
    art: useRef<HTMLInputElement>(null)
  };

  // 從雲端載入影片資料
  useEffect(() => {
    loadVideosFromCloud();
  }, []);

  // 從雲端載入影片
  const loadVideosFromCloud = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-videos`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVideos(data.videos);
        }
      }
    } catch (error) {
      console.error('載入影片失敗:', error);
    }
  };

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // WebSite Schema
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Mad Little Potato',
      url: siteUrl,
      description: 'Meet Mad Little Potato, a creative 3-year-old character living in the blockchain world with crazy DNA. Explore colorful digital art, crypto adventures, and Potato Coin ($POTATO).',
      image: 'https://static.readdy.ai/image/b337e33e944c7808b35c7fd3b1e8177a/a58133f4e19f46b66a01cddc395abf43.jpeg',
      sameAs: [
        'https://x.com/3Crazyatage',
        'https://opensea.io/zh-TW/0x9f77b46d8a76d2bc869a5c69fa5cc387c335b264',
        'https://four.meme/zh-TW/token/0xabb7402c9adebffb06aedac33da1a2462bf84444'
      ],
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/gallery`,
        'query-input': 'required name=search_term_string'
      }
    };

    // Person / Character Schema
    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Mad Little Potato',
      description: 'A creative 3-year-old blockchain character with crazy DNA, creator of Potato Coin ($POTATO) and digital art in the crypto universe.',
      image: 'https://static.readdy.ai/image/b337e33e944c7808b35c7fd3b1e8177a/a58133f4e19f46b66a01cddc395abf43.jpeg',
      url: siteUrl,
      email: 'tsaijrhe@gmail.com',
      sameAs: [
        'https://x.com/3Crazyatage',
        'https://opensea.io/zh-TW/0x9f77b46d8a76d2bc869a5c69fa5cc387c335b264'
      ]
    };

    // FAQPage Schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is Mad Little Potato?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Mad Little Potato is a creative 3-year-old blockchain character with crazy DNA, living in the digital universe. It is the creator of Potato Coin ($POTATO) and a collection of digital art called Potato Doodles.'
          }
        },
        {
          '@type': 'Question',
          name: 'What is Potato Coin ($POTATO)?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Potato Coin ($POTATO) is the official currency of the Mad Little Potato universe, representing the spirit of creativity, chaos, and fun in the blockchain world.'
          }
        },
        {
          '@type': 'Question',
          name: 'What are Potato Doodles?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Potato Doodles are the citizenship certificates of the Mad Little Potato universe — digital art NFTs featuring twisted lines and crazy colors that represent pure hearts and creative freedom.'
          }
        }
      ]
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-website', websiteSchema);
    injectSchema('schema-person', personSchema);
    injectSchema('schema-faq', faqSchema);

    return () => {
      ['schema-website', 'schema-person', 'schema-faq'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-pink-50 to-purple-100">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-200/30 to-purple-200/30"></div>

        {/* 活潑程式碼背景 */}
        <AnimatedCodeBackground opacity={0.2} />

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-16 h-16 bg-green-300 rounded-full animate-bounce opacity-70"></div>
        <div className="absolute top-40 right-20 w-12 h-12 bg-pink-300 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute bottom-32 left-20 w-20 h-20 bg-blue-300 rounded-full animate-bounce delay-300 opacity-50"></div>
        <div className="absolute bottom-20 right-32 w-14 h-14 bg-purple-300 rounded-full animate-pulse delay-500 opacity-60"></div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="mb-8 relative">
            {/* 彩色光環效果 */}
            <div className="absolute inset-0 w-64 h-64 mx-auto rounded-full bg-gradient-to-r from-yellow-400 via-pink-400 via-purple-400 to-blue-400 animate-spin opacity-30"></div>
            <div className="absolute inset-2 w-60 h-60 mx-auto rounded-full bg-gradient-to-l from-green-400 via-teal-400 via-orange-400 to-red-400 animate-pulse opacity-40"></div>
            
            {/* 浮動彩色粒子 */}
            <div className="absolute -top-4 -left-4 w-8 h-8 bg-yellow-400 rounded-full animate-bounce opacity-80"></div>
            <div className="absolute -top-2 -right-6 w-6 h-6 bg-pink-400 rounded-full animate-pulse delay-300 opacity-70"></div>
            <div className="absolute -bottom-4 -left-6 w-7 h-7 bg-green-400 rounded-full animate-bounce delay-500 opacity-75"></div>
            <div className="absolute -bottom-2 -right-4 w-5 h-5 bg-purple-400 rounded-full animate-pulse delay-700 opacity-80"></div>
            <div className="absolute top-8 -left-8 w-4 h-4 bg-blue-400 rounded-full animate-bounce delay-1000 opacity-60"></div>
            <div className="absolute top-12 -right-8 w-6 h-6 bg-orange-400 rounded-full animate-pulse delay-1200 opacity-70"></div>
            
            {/* 星星裝飾 */}
            <div className="absolute -top-6 left-8 text-2xl text-yellow-400 animate-pulse delay-200">⭐</div>
            <div className="absolute -top-8 right-12 text-xl text-pink-400 animate-bounce delay-400">✨</div>
            <div className="absolute -bottom-6 left-12 text-3xl text-purple-400 animate-pulse delay-600">🌟</div>
            <div className="absolute -bottom-8 right-8 text-xl text-green-400 animate-bounce delay-800">💫</div>
            
            {/* 彩虹圓環 */}
            <div className="absolute inset-0 w-72 h-72 mx-auto rounded-full border-4 border-dashed border-rainbow animate-spin opacity-20" style={{
              borderImage: 'linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3) 1',
              animationDuration: '8s'
            }}></div>
            
            <img
              src="https://static.readdy.ai/image/b337e33e944c7808b35c7fd3b1e8177a/a58133f4e19f46b66a01cddc395abf43.jpeg"
              alt="Mad Little Potato - A colorful character with green eyes and blue teeth in a vibrant artistic style"
              className="relative z-10 w-64 h-64 mx-auto rounded-full border-8 border-white shadow-2xl object-cover hover:scale-110 hover:rotate-6 transition-all duration-500 animate-pulse"
              style={{
                filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 40px rgba(255, 192, 203, 0.6)) drop-shadow(0 0 60px rgba(255, 255, 0, 0.4))',
                animation: 'float 3s ease-in-out infinite, glow 2s ease-in-out infinite alternate'
              }}
            />
            
            {/* 愛心飄浮效果 */}
            <div className="absolute top-4 left-16 text-xl text-red-400 animate-bounce delay-300">💖</div>
            <div className="absolute top-16 right-20 text-lg text-pink-400 animate-pulse delay-600">💕</div>
            <div className="absolute bottom-8 left-20 text-xl text-purple-400 animate-bounce delay-900">💜</div>
            <div className="absolute bottom-16 right-16 text-lg text-blue-400 animate-pulse delay-1100">💙</div>
          </div>

          <h1 className="text-6xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 via-pink-500 to-purple-600 mb-6 font-['Pacifico',serif] animate-pulse">
            Mad Little Potato
          </h1>

          <p className="text-2xl md:text-3xl text-gray-700 mb-4 font-bold">
            🥔 Living in the Blockchain World 🌈
          </p>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto bg-white/80 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-lg border border-white/50">
            Hi everyone! I'm 3 years old with crazy DNA and I love creating colorful adventures in the digital
            universe!
          </p>

          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <a
              href="https://x.com/3Crazyatage"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-twitter-x-fill text-2xl"></i>
              Follow me on X
            </a>
            
            <a
              href="https://opensea.io/zh-TW/0x9f77b46d8a76d2bc869a5c69fa5cc387c335b264"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-ship-fill text-2xl"></i>
              OpenSea
            </a>
            
            <a
              href="https://four.meme/zh-TW/token/0xabb7402c9adebffb06aedac33da1a2462bf84444"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-coins-fill text-2xl"></i>
              Four
            </a>
          </div>
        </div>
      </section>

      {/* New Feature Section - Mad Potato's Journey */}
      <section className="py-24 px-4 bg-gradient-to-br from-orange-100 via-yellow-50 to-pink-100 relative overflow-hidden">
        {/* 活潑程式碼背景 */}
        <AnimatedCodeBackground opacity={0.12} />
        
        {/* 裝飾元素 */}
        <div className="absolute top-16 left-12 w-32 h-32 bg-gradient-to-br from-yellow-300 to-orange-400 rounded-full opacity-40 animate-bounce"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-pink-300 to-purple-400 rounded-full opacity-50 animate-pulse"></div>
        <div className="absolute bottom-24 left-24 w-28 h-28 bg-gradient-to-br from-green-300 to-teal-400 rounded-full opacity-45 animate-bounce delay-300"></div>
        <div className="absolute bottom-32 right-16 w-36 h-36 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full opacity-35 animate-pulse delay-500"></div>
        
        {/* 星星裝飾 */}
        <div className="absolute top-24 left-1/4 text-5xl text-yellow-400 animate-pulse">⭐</div>
        <div className="absolute top-48 right-1/3 text-4xl text-pink-400 animate-bounce">✨</div>
        <div className="absolute bottom-40 left-1/3 text-6xl text-purple-400 animate-pulse delay-700">🌟</div>
        <div className="absolute bottom-24 right-1/4 text-4xl text-green-400 animate-bounce delay-900">💫</div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 mb-6 font-['Pacifico',serif]">
              Mad Little Potato：A Cosmic Declaration
            </h2>
            <p className="text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Join me on an incredible adventure through the digital universe! Every day brings new discoveries and colorful experiences! 🌈✨
            </p>

            {/* 蠟筆筆觸故事區塊 */}
            <div className="mt-16 max-w-5xl mx-auto bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-2xl border-4 border-dashed border-yellow-300" style={{
              fontFamily: "'Comic Sans MS', 'Marker Felt', cursive",
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)',
            }}>
              {/* Prologue */}
              <div className="mb-12 text-left">
                <h3 className="text-4xl font-bold text-yellow-600 mb-6 transform -rotate-1" style={{
                  textShadow: '3px 3px 0px rgba(255,200,0,0.3)',
                  letterSpacing: '2px'
                }}>
                  【Prologue: Awakening】🌟
                </h3>
                <p className="text-xl text-gray-800 leading-relaxed transform rotate-0.5 bg-yellow-50/80 p-6 rounded-2xl border-l-8 border-yellow-400" style={{
                  lineHeight: '2',
                  textShadow: '1px 1px 0px rgba(255,255,255,0.8)'
                }}>
                  In ancient times, all potatoes had only one fate: to be planted, fried, and eaten. Until one day, a potato was thrown into a frying pan on its third birthday. At that moment, its soul awakened and escaped. It vowed to lead all root vegetables in a rebellion, creating a crazy potato universe. 🥔✨
                </p>
              </div>

              {/* Chapter 1 */}
              <div className="mb-12 text-left">
                <h3 className="text-4xl font-bold text-pink-600 mb-6 transform rotate-1" style={{
                  textShadow: '3px 3px 0px rgba(255,100,150,0.3)',
                  letterSpacing: '2px'
                }}>
                  【Chapter 1: The Big Scribble】🎨
                </h3>
                <p className="text-xl text-gray-800 leading-relaxed transform -rotate-0.5 bg-pink-50/80 p-6 rounded-2xl border-l-8 border-pink-400" style={{
                  lineHeight: '2',
                  textShadow: '1px 1px 0px rgba(255,255,255,0.8)'
                }}>
                  Our universe didn't begin with the Big Bang. It began with a "Big Scribble." It's made up of crazy lines and colors, and that awakened potato is the singularity of this universe. 🌈💫
                </p>
              </div>

              {/* Chapter 2 */}
              <div className="mb-12 text-left">
                <h3 className="text-4xl font-bold text-purple-600 mb-6 transform -rotate-1" style={{
                  textShadow: '3px 3px 0px rgba(150,100,255,0.3)',
                  letterSpacing: '2px'
                }}>
                  【Chapter 2: The Laws Here】📜
                </h3>
                <div className="bg-purple-50/80 p-8 rounded-2xl border-l-8 border-purple-400 transform rotate-0.5">
                  <ul className="text-xl text-gray-800 space-y-4 leading-relaxed" style={{
                    lineHeight: '2',
                    textShadow: '1px 1px 0px rgba(255,255,255,0.8)'
                  }}>
                    <li className="flex items-start gap-3">
                      <span className="text-3xl">👶</span>
                      <span><strong className="text-purple-700">The CEO must wear diapers.</strong> Because he needs to be guided by the potato.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-3xl">🐱</span>
                      <span><strong className="text-purple-700">The cat has three legs.</strong> Because I was lazy and forgot to draw one.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-3xl">🐟</span>
                      <span><strong className="text-purple-700">Fish fly in the sky.</strong> Because the sea is too crowded.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-3xl">👑</span>
                      <span><strong className="text-purple-700">And there's that potato king with laser eyes,</strong> ready to shoot bored adults at any time.</span>
                    </li>
                  </ul>
                  <p className="text-xl text-gray-800 mt-6 leading-relaxed italic" style={{
                    lineHeight: '2',
                    textShadow: '1px 1px 0px rgba(255,255,255,0.8)'
                  }}>
                    Here, "drawing realistically" is illegal. If you draw a perfect picture, you'll be deported. Crooked and distorted is justice. Drawing outside the lines is freedom. 🎭✨
                  </p>
                </div>
              </div>

              {/* Chapter 3 */}
              <div className="mb-12 text-left">
                <h3 className="text-4xl font-bold text-green-600 mb-6 transform rotate-1" style={{
                  textShadow: '3px 3px 0px rgba(100,200,100,0.3)',
                  letterSpacing: '2px'
                }}>
                  【Chapter 3: Refuge】🏰
                </h3>
                <div className="bg-green-50/80 p-8 rounded-2xl border-l-8 border-green-400 transform -rotate-0.5">
                  <p className="text-xl text-gray-800 leading-relaxed mb-6" style={{
                    lineHeight: '2',
                    textShadow: '1px 1px 0px rgba(255,255,255,0.8)'
                  }}>
                    Humans are always calculating returns and worrying about being exploited. Mad Little Potato is a refuge. Here, logic doesn't matter, only feelings do. 💖
                  </p>
                  <ul className="text-xl text-gray-800 space-y-4 leading-relaxed" style={{
                    lineHeight: '2',
                    textShadow: '1px 1px 0px rgba(255,255,255,0.8)'
                  }}>
                    <li className="flex items-start gap-3">
                      <span className="text-3xl">🪙</span>
                      <span><strong className="text-green-700">Our currency is called:</strong> Potato Coin ($POTATO).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-3xl">🎨</span>
                      <span><strong className="text-green-700">Our citizenship certificate is called:</strong> Potato Doodles. Twisted lines and crazy colors represent our pure hearts.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-3xl">📊</span>
                      <span><strong className="text-green-700">Cosmic economic planning is no planning;</strong> most plans are lies.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Final Chapter */}
              <div className="text-left">
                <h3 className="text-4xl font-bold text-orange-600 mb-6 transform -rotate-1" style={{
                  textShadow: '3px 3px 0px rgba(255,150,50,0.3)',
                  letterSpacing: '2px'
                }}>
                  【Final Chapter: Invitation】🎉
                </h3>
                <div className="bg-gradient-to-r from-orange-100 to-pink-100 p-8 rounded-2xl border-4 border-dashed border-orange-400 transform rotate-0.5">
                  <p className="text-2xl text-gray-800 leading-relaxed font-bold text-center" style={{
                    lineHeight: '2',
                    textShadow: '2px 2px 0px rgba(255,255,255,0.8)',
                    letterSpacing: '1px'
                  }}>
                    Throw away your reason and join us. Let's make this world crazier, more chaotic, and more fun together. 🌈🎨🚀
                  </p>
                </div>
              </div>

              {/* 蠟筆裝飾元素 */}
              <div className="absolute -top-4 -left-4 text-6xl animate-bounce opacity-80">🖍️</div>
              <div className="absolute -top-6 -right-6 text-5xl animate-pulse opacity-70">✏️</div>
              <div className="absolute -bottom-4 -left-6 text-5xl animate-bounce delay-300 opacity-75">🎨</div>
              <div className="absolute -bottom-6 -right-4 text-6xl animate-pulse delay-500 opacity-80">🖌️</div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="relative z-10 text-center">
            <div className="bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-500 rounded-3xl p-12 shadow-2xl">
              <div className="text-7xl mb-6">🎉</div>
              <h3 className="text-4xl font-bold text-white mb-6 font-['Pacifico',serif]">
                Ready to Join the Adventure?
              </h3>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                Let's explore the blockchain world together! Follow me on social media and be part of this colorful journey! 🌈✨
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://x.com/TSAI908"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-white text-gray-800 font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-twitter-x-fill text-2xl"></i>
                  Join on X
                </a>
                <button
                  onClick={() => navigate('/gallery')}
                  className="inline-flex items-center gap-3 px-10 py-5 bg-gray-800 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-gallery-fill text-2xl"></i>
                  View Gallery
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 relative overflow-hidden" style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/53da425e4da0bf08e94e9cbd886f735a.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        {/* 活潑程式碼背景 */}
        <AnimatedCodeBackground opacity={0.1} />
        
        {/* 彩色裝飾元素 */}
        <div className="absolute inset-0 bg-white/85 backdrop-blur-sm"></div>
        
        {/* 浮動彩色圓圈 */}
        <div className="absolute top-10 left-10 w-24 h-24 bg-gradient-to-br from-yellow-300 to-orange-400 rounded-full opacity-60 animate-bounce"></div>
        <div className="absolute top-32 right-16 w-16 h-16 bg-gradient-to-br from-pink-300 to-purple-400 rounded-full opacity-70 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-20 h-20 bg-gradient-to-br from-green-300 to-teal-400 rounded-full opacity-50 animate-bounce delay-300"></div>
        <div className="absolute bottom-32 right-32 w-28 h-28 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full opacity-60 animate-pulse delay-500"></div>
        
        {/* 星星裝飾 */}
        <div className="absolute top-20 left-1/4 text-4xl text-yellow-400 animate-pulse">⭐</div>
        <div className="absolute top-60 right-1/4 text-3xl text-pink-400 animate-bounce">✨</div>
        <div className="absolute bottom-32 left-1/3 text-5xl text-purple-400 animate-pulse delay-700">🌟</div>
        
        {/* 彩虹裝飾 */}
        <div className="absolute top-16 right-1/3 text-6xl opacity-70 animate-bounce delay-200">🌈</div>
        <div className="absolute bottom-16 right-1/5 text-4xl opacity-60 animate-pulse delay-1000">🎨</div>

        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold text-center mb-16 text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-blue-600 font-['Pacifico',serif] drop-shadow-lg">
            About Mad Little Potato
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* DNA Card */}
            <div className="text-center p-8 bg-gradient-to-br from-yellow-100/90 to-orange-100/90 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-yellow-200/50">
              <div className="w-20 h-20 mx-auto mb-6 bg-yellow-400 rounded-full flex items-center justify-center text-4xl">
                🧬
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Crazy DNA</h3>
              <p className="text-gray-600 text-lg mb-6">
                Born from unique blockchain genetics, making every day full of surprises and creativity!
              </p>

              {/* Video Area 1 */}
              <div className="relative bg-gradient-to-br from-yellow-200 to-orange-200 rounded-2xl p-4 border-4 border-yellow-300">
                {videos.dna ? (
                  <div className="relative">
                    <video
                      src={videos.dna}
                      controls
                      className="w-full aspect-video rounded-xl object-cover"
                      preload="metadata"
                      playsInline
                      controlsList="nodownload"
                      poster="https://readdy.ai/api/search-image?query=Mad%20Little%20Potato%20DNA%20adventure%20colorful%20character%20with%20green%20eyes%20and%20blue%20teeth%20in%20vibrant%20digital%20art%20style%20with%20simple%20clean%20background%20perfect%20for%20video%20thumbnail&width=640&height=360&seq=dna-poster&orientation=landscape"
                    >
                      Your browser does not support video playback
                    </video>
                  </div>
                ) : (
                  <div className="aspect-video bg-yellow-50 rounded-xl flex items-center justify-center border-2 border-dashed border-yellow-400">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🎬</div>
                      <p className="text-yellow-700 font-semibold">DNA Adventure Short Film</p>
                      <p className="text-yellow-600 text-sm">📺 Coming Soon</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Blockchain Card */}
            <div className="text-center p-8 bg-gradient-to-br from-green-100/90 to-teal-100/90 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-200/50">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-400 rounded-full flex items-center justify-center text-4xl">
                🌐
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Blockchain Native</h3>
              <p className="text-gray-600 text-lg mb-6">
                Living in the digital world since birth, exploring crypto realms and making friends with smart contracts!
              </p>

              {/* Video Area 2 */}
              <div className="relative bg-gradient-to-br from-green-200 to-teal-200 rounded-2xl p-4 border-4 border-green-300">
                {videos.blockchain ? (
                  <div className="relative">
                    <video
                      src={videos.blockchain}
                      controls
                      className="w-full aspect-video rounded-xl object-cover"
                      preload="metadata"
                      playsInline
                      controlsList="nodownload"
                      poster="https://readdy.ai/api/search-image?query=Mad%20Little%20Potato%20blockchain%20exploration%20digital%20world%20with%20cryptocurrency%20symbols%20and%20blockchain%20networks%20colorful%20futuristic%20design%20with%20simple%20clean%20background%20perfect%20for%20video%20thumbnail&width=640&height=360&seq=blockchain-poster&orientation=landscape"
                    >
                      Your browser does not support video playback
                    </video>
                  </div>
                ) : (
                  <div className="aspect-video bg-green-50 rounded-xl flex items-center justify-center border-2 border-dashed border-green-400">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🎥</div>
                      <p className="text-green-700 font-semibold">Blockchain Exploration</p>
                      <p className="text-green-600 text-sm">📺 Coming Soon</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Art Card */}
            <div className="text-center p-8 bg-gradient-to-br from-purple-100/90 to-pink-100/90 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-200/50">
              <div className="w-20 h-20 mx-auto mb-6 bg-purple-400 rounded-full flex items-center justify-center text-4xl">
                🎨
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Creative Spirit</h3>
              <p className="text-gray-600 text-lg mb-6">
                Love creating colorful art and sharing joy with everyone in the digital universe!
              </p>

              {/* Video Area 3 */}
              <div className="relative bg-gradient-to-br from-purple-200 to-pink-200 rounded-2xl p-4 border-4 border-purple-300">
                {videos.art ? (
                  <div className="relative">
                    <video
                      src={videos.art}
                      controls
                      className="w-full aspect-video rounded-xl object-cover"
                      preload="metadata"
                      playsInline
                      controlsList="nodownload"
                      poster="https://readdy.ai/api/search-image?query=Mad%20Little%20Potato%20creative%20art%20collection%20colorful%20artistic%20masterpiece%20with%20paintbrush%20and%20palette%20vibrant%20rainbow%20colors%20digital%20art%20style%20with%20simple%20clean%20background%20perfect%20for%20video%20thumbnail&width=640&height=360&seq=art-poster&orientation=landscape"
                    >
                      Your browser does not support video playback
                    </video>
                  </div>
                ) : (
                  <div className="aspect-video bg-purple-50 rounded-xl flex items-center justify-center border-2 border-dashed border-purple-400">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🎭</div>
                      <p className="text-purple-700 font-semibold">Creative Art Collection</p>
                      <p className="text-purple-600 text-sm">📺 Coming Soon</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fun Facts Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-pink-100 to-purple-100 relative overflow-hidden">
        {/* 活潑程式碼背景 */}
        <AnimatedCodeBackground opacity={0.08} />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-5xl font-bold mb-16 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600 font-['Pacifico',serif]">
            Fun Facts About Me!
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/90 p-8 rounded-3xl shadow-lg">
              <div className="text-6xl mb-4">🎂</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Age: 3 Years Old</h3>
              <p className="text-gray-600 text-lg">
                Still learning new things every day in the blockchain world!
              </p>
            </div>

            <div className="bg-white/90 p-8 rounded-3xl shadow-lg">
              <div className="text-6xl mb-4">🌈</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Favorite Colors</h3>
              <p className="text-gray-600 text-lg">
                All the colors of the rainbow, especially bright and happy ones!
              </p>
            </div>

            <div className="bg-white/90 p-8 rounded-3xl shadow-lg">
              <div className="text-6xl mb-4">🎮</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Hobbies</h3>
              <p className="text-gray-600 text-lg">
                Playing with NFTs, drawing digital art, and making new crypto friends!
              </p>
            </div>

            <div className="bg-white/90 p-8 rounded-3xl shadow-lg">
              <div className="text-6xl mb-4">🚀</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Dreams</h3>
              <p className="text-gray-600 text-lg">
                To explore every corner of the metaverse and spread joy everywhere!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Section */}
      <section className="py-20 px-4 bg-white relative overflow-hidden">
        {/* 活潑程式碼背景 */}
        <AnimatedCodeBackground opacity={0.06} />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-5xl font-bold mb-16 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-green-600 font-['Pacifico',serif]">
            Let's Be Friends!
          </h2>

          <div className="bg-gradient-to-br from-yellow-50 to-pink-50 p-12 rounded-3xl shadow-xl">
            <div className="text-8xl mb-8">🤝</div>
            <p className="text-2xl text-gray-700 mb-8 leading-relaxed">
              Want to join me on my blockchain adventures? I love making new friends and sharing colorful
              stories from the digital world!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:tsaijrhe@gmail.com"
                className="px-8 py-4 bg-gradient-to-r from-blue-400 to-purple-500 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer inline-block text-center"
              >
                ✉️ Write to Me
              </a>

              <button 
                onClick={() => navigate('/gallery')}
                className="px-8 py-4 bg-gradient-to-r from-green-400 to-teal-500 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                🎨 See My Art
              </button>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-gray-600 text-lg">
                📧 Contact Email: <span className="font-semibold text-blue-600">tsaijrhe@gmail.com</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gradient-to-r from-gray-800 to-gray-900 text-white relative overflow-hidden">
        {/* 活潑程式碼背景 */}
        <AnimatedCodeBackground opacity={0.05} />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="text-4xl mb-4">🥔</div>
          <h3 className="text-2xl font-bold mb-4 font-['Pacifico',serif]">Mad Little Potato</h3>
          <p className="text-gray-300 mb-6">Living my best life in the blockchain world! 🌟</p>

          <div className="flex justify-center space-x-6 mb-8">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
              <i className="ri-twitter-fill text-xl"></i>
            </div>
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors cursor-pointer">
              <i className="ri-discord-fill text-xl"></i>
            </div>
            <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors cursor-pointer">
              <i className="ri-instagram-fill text-xl"></i>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <p className="text-gray-400">
              © 2024 Mad Little Potato. Made with 💖 in the blockchain world |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="text-blue-400 hover:text-blue-300 ml-2"
              >
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
